import React from "react";
import { ArrowLeft, Loader2, Download, Pencil, Eye } from "lucide-react";

/**
 * Top bar of the form editor: back + title/status on the left; on the right a
 * Fill/View segmented control and the single primary action (Download, which
 * silently bakes the answers). "Edit" returns from the finalized/downloaded
 * read-only view. AI + add tools live in the floating canvas toolbar.
 */
const FormReviewTopBar = ({
  title,
  statusLine,
  onBack,
  detecting,
  viewMode,
  onSwitchToFill,
  onSwitchToView,
  loading,
  job,
  isFinalizedView,
  hasDownloaded,
  fillStage,
  filling,
  downloading,
  onDownload,
  onBackToEdit,
}) => {
  const disabled = loading || !job;

  const downloadBusy = filling || downloading;
  const busyLabel =
    fillStage === "filling"
      ? "Generating…"
      : fillStage === "finalizing" && downloading
      ? "Preparing…"
      : fillStage === "finalizing"
      ? "Saving…"
      : "Downloading…";

  const downloadBtn = downloadBusy ? (
    <button
      disabled
      className="inline-flex items-center gap-2 h-10 px-5 rounded-xl text-[14px] font-semibold text-white bg-[#C95EBE] opacity-80 cursor-not-allowed shadow-sm"
    >
      <Loader2 size={16} className="animate-spin" />
      {busyLabel}
    </button>
  ) : (
    <button
      onClick={onDownload}
      disabled={disabled}
      className="group inline-flex items-center gap-2 h-10 px-5 rounded-xl text-[14px] font-semibold text-white bg-gradient-to-b from-[#C95EBE] to-[#b04da6] shadow-[0_2px_8px_rgba(201,94,190,0.35)] hover:shadow-[0_4px_14px_rgba(201,94,190,0.45)] hover:brightness-[1.03] active:scale-[0.98] transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C95EBE]/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
    >
      <Download size={16} className="transition-transform group-hover:translate-y-px" />
      Download
    </button>
  );

  return (
    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 text-[#52525B] transition-colors"
          title="Back to Forms"
          aria-label="Back to Forms"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0">
          <h1 className="text-[16px] font-semibold text-[#18101C] truncate leading-tight">
            {title}
          </h1>
          {statusLine ? (
            <p className="text-[12px] text-[#8A8694] mt-0.5">{statusLine}</p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Back to editing from the finalized / downloaded read-only view. */}
        {!detecting && (isFinalizedView || hasDownloaded) && (
          <button
            onClick={onBackToEdit}
            disabled={disabled}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[14px] font-medium text-[#52525B] bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Pencil size={15} />
            Edit
          </button>
        )}

        {/* AI auto-fill in progress */}
        {detecting && (
          <span className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[14px] font-medium text-[#C95EBE] bg-[#FDF2FB] border border-[#F3D2EC]">
            <Loader2 size={16} className="animate-spin" />
            Auto-filling…
          </span>
        )}

        {/* Editing actions */}
        {!detecting && !hasDownloaded && (
          <>
            {/* Fill / View segmented control */}
            {!isFinalizedView && (
              <div className="inline-flex items-center gap-0.5 p-1 rounded-xl bg-gray-100 border border-gray-200/70">
                {[
                  { key: "fill", label: "Fill", Icon: Pencil, onClick: onSwitchToFill },
                  { key: "view", label: "View", Icon: Eye, onClick: onSwitchToView },
                ].map(({ key, label, Icon, onClick }) => {
                  const active = viewMode === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={onClick}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                        active
                          ? "bg-white text-[#C95EBE] shadow-sm"
                          : "text-[#71717A] hover:text-[#18101C]"
                      }`}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            {downloadBtn}
          </>
        )}
      </div>
    </div>
  );
};

export default FormReviewTopBar;
